import { useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useAccessOwner } from '../access/access-owner-provider'
import { keyOf } from '../access/access-owner'
import { parseAccessUrl } from '../access/url'
import { useBoot } from './boot-provider'

const BARE_ROOT = '/'

/**
 * Makes the URL decide the open domain or organization. It resolves through the access owner
 * whenever the domain or organization the path names changes; a change to the sub-route, query or
 * hash inside the same one does nothing. It only reads the router and the owner, and never writes
 * AppState. Boot resolves the first URL, so the owner answers a repeat without a request.
 *
 * A path that names neither, such as `/users/5` or `/integrations`, keeps whatever is open. Only the
 * bare root is sent to the open domain's dashboard.
 */
export const AccessUrlSync = () => {
    const { phase } = useBoot()
    const owner = useAccessOwner()
    const history = useHistory()
    const { pathname } = useLocation()
    const ready = phase.phase === 'ready'
    const key = keyOf(parseAccessUrl(pathname))
    // The effect runs when the key changes, not on every sub-route change, and reads the path as it is then.
    const pathnameRef = useRef(pathname)
    pathnameRef.current = pathname

    useEffect(() => {
        if (!ready) {
            return
        }

        const path = pathnameRef.current
        const status = owner.resolution.status
        let superseded = false

        if (key === 'none' && (status === 'resolved' || status === 'resolving')) {
            if (path === BARE_ROOT && owner.domain) {
                history.replace(`/domains/${owner.domain.id}/dashboard`)
            }
            return
        }

        void owner.resolveUrl(path).then((result) => {
            if (superseded || result.status !== 'resolved' || !result.redirectTo || path !== BARE_ROOT) {
                return
            }
            history.replace(result.redirectTo)
        })

        return () => {
            superseded = true
        }
    }, [ready, key, history, owner])

    return null
}
